import React, { useState } from "react";
import { Box, Divider, Typography, IconButton, Collapse } from "@mui/material";
import { useTheme } from "@emotion/react";
import DocumentPanel from "../components/document-panel"; 
import ConfigurationPanel from "../components/configuration-panel"; 
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const Sidebar = ({ activePanel, setActivePanel }) => {
    const theme = useTheme();
    const [isInputFocused, setInputFocus] = useState(false); // State to track if any input is focused
    const [sidebarWidth, setSidebarWidth] = useState(550); // Initial width of the sidebar
    const [isResizing, setIsResizing] = useState(false); // Track if the user is currently resizing

    // Handle the mouse down event for resizing
    const handleMouseDown = (e) => {
        setIsResizing(true);
        document.body.style.cursor = "ew-resize"; // Change cursor to indicate resizing
    };

    // Handle mouse move event to resize the sidebar
    const handleMouseMove = (e) => {
        if (isResizing) {
            // Update sidebar width based on mouse movement
            const newWidth = Math.max(300, e.clientX); // Minimum width is 300px
            setSidebarWidth(newWidth);
        }
    };

    // Handle the mouse up event to stop resizing
    const handleMouseUp = () => {
        setIsResizing(false);
        document.body.style.cursor = "default"; // Reset cursor when resizing is finished
    };

    React.useEffect(() => {
        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizing]);

    const togglePanel = (panel) => {
        setActivePanel(activePanel === panel ? null : panel); // Toggle the active panel
    };

    return (
        <Box
            sx={{
                width: sidebarWidth, // Dynamic width based on state
                height: "100%",
                backgroundColor: theme.palette.primary.light,
                color: "#fff",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflowY: "auto",
                transition: "width 0.3s ease",
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                margin: "10px",
            }}
        >
            <Box
                sx={{
                    padding: "16px 20px",
                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.dark,
                        fontSize: "1.2rem",
                    }}
                >
                    Meny
                </Typography>
            </Box>

            <Box sx={{ padding: "0 16px", display: "flex", flexDirection: "column", flex: 1 }}>
                <Box 
                    sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "space-between",
                        padding: "12px 0",
                        cursor: "pointer",
                        borderBottom: `1px solid ${theme.palette.primary.main}20`,
                        transition: "background-color 0.2s",
                        "&:hover": {
                            backgroundColor: `${theme.palette.primary.main}10`,
                        }
                    }}
                    onClick={() => togglePanel("documents")}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <DescriptionIcon sx={{ color: theme.palette.primary.dark, marginRight: "8px" }} />
                        <Typography sx={{ color: theme.palette.primary.dark, fontWeight: 500 }}>
                            Dokument
                        </Typography>
                    </Box>
                    <IconButton size="small" sx={{ color: theme.palette.primary.dark }}>
                        {activePanel === "documents" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
                
                <Collapse in={activePanel === "documents"} timeout={300} unmountOnExit>
                    <Box sx={{ padding: "8px 0 16px 0" }}>
                        <DocumentPanel /> 
                    </Box>
                </Collapse>

                <Box 
                    sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "space-between",
                        padding: "12px 0",
                        cursor: "pointer",
                        borderBottom: `1px solid ${theme.palette.primary.main}20`,
                        transition: "background-color 0.2s",
                        "&:hover": {
                            backgroundColor: `${theme.palette.primary.main}10`,
                        }
                    }}
                    onClick={() => togglePanel("config")}
                >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <SettingsIcon sx={{ color: theme.palette.primary.dark, marginRight: "8px" }} />
                        <Typography sx={{ color: theme.palette.primary.dark, fontWeight: 500 }}>
                            Generera rapport
                        </Typography>
                    </Box>
                    <IconButton size="small" sx={{ color: theme.palette.primary.dark }}>
                        {activePanel === "config" ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
                
                <Collapse in={activePanel === "config"} timeout={300} unmountOnExit>
                    <Box sx={{ 
                        padding: "8px 0 16px 0",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        height: "100%",
                    }}>
                        <ConfigurationPanel 
                            isInputFocused={isInputFocused} 
                            setInputFocus={setInputFocus} 
                        /> 
                    </Box>
                </Collapse>
            </Box>

            <Box
                onMouseDown={handleMouseDown}
                sx={{
                    position: "absolute",
                    right: 0,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "8px",
                    height: "60px",
                    cursor: "ew-resize",
                    backgroundColor: theme.palette.primary.main,
                    opacity: 0.7,
                    borderRadius: "0 4px 4px 0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                    transition: "opacity 0.2s, width 0.2s",
                    "&:hover": {
                        opacity: 1,
                        width: "12px",
                    }
                }}
            >
                <DragIndicatorIcon sx={{ color: "#fff", fontSize: "16px" }} />
            </Box>
        </Box>
    );
};

export default Sidebar;
